<template>
  <div class="two-factor-auth">
    <ion-list>
      <ion-list-header>
        <ion-label>
          <h2>{{ t('twoFactorAuth') }}</h2>
          <p>{{ t('twoFactorAuthDescription') }}</p>
        </ion-label>
      </ion-list-header>

      <!-- MFA Status -->
      <ion-item v-if="!isEnrolled && !showEnrollment">
        <ion-icon :icon="shieldCheckmarkOutline" slot="start" color="medium"></ion-icon>
        <ion-label>
          <h3>{{ t('twoFactorDisabled') }}</h3>
          <p>{{ t('twoFactorDisabledDesc') }}</p>
        </ion-label>
        <ion-button @click="startEnrollment" slot="end">
          {{ t('enableTwoFactor') }}
        </ion-button>
      </ion-item>

      <ion-item v-if="isEnrolled && !showEnrollment">
        <ion-icon :icon="shieldCheckmarkOutline" slot="start" color="success"></ion-icon>
        <ion-label>
          <h3>{{ t('twoFactorEnabled') }}</h3>
          <p>{{ t('twoFactorEnabledDesc') }}</p>
        </ion-label>
        <ion-button @click="disableMFA" color="danger" fill="outline" slot="end">
          {{ t('disableTwoFactor') }}
        </ion-button>
      </ion-item>

      <!-- Enrollment Flow -->
      <div v-if="showEnrollment" class="enrollment-section">
        <ion-card>
          <ion-card-header>
            <ion-card-title>{{ t('setupTwoFactor') }}</ion-card-title>
            <ion-card-subtitle>{{ t('setupTwoFactorStep1') }}</ion-card-subtitle>
          </ion-card-header>

          <ion-card-content>
            <!-- Step 1: QR Code -->
            <div v-if="enrollmentData && enrollmentStep === 1" class="qr-section">
              <p class="ion-text-center">{{ t('scanQRCode') }}</p>
              
              <!-- QR Code Display -->
              <div class="qr-code-container">
                <img :src="enrollmentData.qrCode" alt="QR Code" class="qr-code" />
              </div>

              <!-- Manual Entry -->
              <ion-accordion-group>
                <ion-accordion value="manual">
                  <ion-item slot="header">
                    <ion-label>{{ t('cantScanQR') }}</ion-label>
                  </ion-item>
                  <div slot="content" class="manual-entry">
                    <p>{{ t('enterManually') }}:</p>
                    <code class="secret-code">{{ enrollmentData.secret }}</code>
                    <ion-button 
                      size="small" 
                      fill="clear" 
                      @click="copySecret"
                    >
                      <ion-icon :icon="copyOutline" slot="start"></ion-icon>
                      {{ t('copy') }}
                    </ion-button>
                  </div>
                </ion-accordion>
              </ion-accordion-group>

              <ion-button 
                expand="block" 
                @click="enrollmentStep = 2"
                class="ion-margin-top"
              >
                {{ t('next') }}
              </ion-button>
            </div>

            <!-- Step 2: Verification -->
            <div v-if="enrollmentStep === 2" class="verification-section">
              <p>{{ t('enterVerificationCode') }}</p>
              
              <ion-item>
                <ion-label position="stacked">{{ t('verificationCode') }}</ion-label>
                <ion-input
                  v-model="verificationCode"
                  type="text"
                  inputmode="numeric"
                  maxlength="6"
                  placeholder="000000"
                  @keyup.enter="verifyEnrollment"
                ></ion-input>
              </ion-item>

              <div class="button-group ion-margin-top">
                <ion-button 
                  fill="outline" 
                  @click="cancelEnrollment"
                  :disabled="isLoading"
                >
                  {{ t('cancel') }}
                </ion-button>
                <ion-button 
                  @click="verifyEnrollment"
                  :disabled="verificationCode.length !== 6 || isLoading"
                >
                  <span v-if="isLoading" class="loading-spinner mr-2"></span>
                  {{ t('verify') }}
                </ion-button>
              </div>
            </div>
          </ion-card-content>
        </ion-card>
      </div>
    </ion-list>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import {
  IonList,
  IonListHeader,
  IonItem,
  IonLabel,
  IonButton,
  IonIcon,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardSubtitle,
  IonCardContent,
  IonInput,
  IonAccordion,
  IonAccordionGroup,
  alertController,
  toastController
} from '@ionic/vue'
import { 
  shieldCheckmarkOutline,
  copyOutline
} from 'ionicons/icons'
import { authService } from '@/services/auth'
import { useI18n } from '@/i18n'

const { t } = useI18n()

const isEnrolled = ref(false)
const showEnrollment = ref(false)
const enrollmentStep = ref(1)
const enrollmentData = ref<any>(null)
const verificationCode = ref('')
const isLoading = ref(false)

onMounted(async () => {
  await checkMFAStatus()
})

async function checkMFAStatus() {
  try {
    const factors = await authService.getMFAFactors()
    isEnrolled.value = factors.length > 0
  } catch (error) {
    console.error('Error checking MFA status:', error)
  }
}

async function startEnrollment() {
  isLoading.value = true
  try {
    const data = await authService.enrollMFA()
    enrollmentData.value = data
    showEnrollment.value = true
    enrollmentStep.value = 1
  } catch (error) {
    console.error('Error starting enrollment:', error)
    const toast = await toastController.create({
      message: t('errorStartingMFA'),
      duration: 3000,
      color: 'danger'
    })
    await toast.present()
  } finally {
    isLoading.value = false
  }
}

async function verifyEnrollment() {
  if (!enrollmentData.value || verificationCode.value.length !== 6) return

  isLoading.value = true
  try {
    await authService.verifyMFAEnrollment(
      enrollmentData.value.id,
      verificationCode.value
    )

    isEnrolled.value = true
    showEnrollment.value = false
    enrollmentData.value = null
    verificationCode.value = ''
    enrollmentStep.value = 1

    const toast = await toastController.create({
      message: t('twoFactorEnabled'),
      duration: 3000,
      color: 'success'
    })
    await toast.present()
  } catch (error) {
    console.error('Error verifying enrollment:', error)
    const toast = await toastController.create({
      message: t('invalidVerificationCode'),
      duration: 3000,
      color: 'danger'
    })
    await toast.present()
  } finally {
    isLoading.value = false
  }
}

function cancelEnrollment() {
  showEnrollment.value = false
  enrollmentData.value = null
  verificationCode.value = ''
  enrollmentStep.value = 1
}

async function disableMFA() {
  const alert = await alertController.create({
    header: t('disableTwoFactorTitle'),
    message: t('disableTwoFactorConfirm'),
    buttons: [
      {
        text: t('cancel'),
        role: 'cancel'
      },
      {
        text: t('disable'),
        role: 'destructive',
        handler: async () => {
          try {
            const factors = await authService.getMFAFactors()
            if (factors.length > 0) {
              await authService.unenrollMFA(factors[0].id)
              isEnrolled.value = false

              const toast = await toastController.create({
                message: t('twoFactorDisabled'),
                duration: 3000,
                color: 'success'
              })
              await toast.present()
            }
          } catch (error) {
            console.error('Error disabling MFA:', error)
            const toast = await toastController.create({
              message: t('errorDisablingMFA'),
              duration: 3000,
              color: 'danger'
            })
            await toast.present()
          }
        }
      }
    ]
  })

  await alert.present()
}

async function copySecret() {
  if (enrollmentData.value?.secret) {
    await navigator.clipboard.writeText(enrollmentData.value.secret)
    const toast = await toastController.create({
      message: t('copiedToClipboard'),
      duration: 2000,
      color: 'success'
    })
    await toast.present()
  }
}
</script>

<style scoped>
.enrollment-section {
  padding: 1rem;
}

.qr-section,
.verification-section {
  padding: 1rem 0;
}

.qr-code-container {
  display: flex;
  justify-content: center;
  margin: 1.5rem 0;
  padding: 1rem;
  background: white;
  border-radius: 8px;
}

.qr-code {
  max-width: 250px;
  width: 100%;
  height: auto;
}

.manual-entry {
  padding: 1rem;
}

.secret-code {
  display: block;
  padding: 0.75rem;
  background: var(--ion-color-light);
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
  word-break: break-all;
  margin: 0.5rem 0;
}

.button-group {
  display: flex;
  gap: 0.5rem;
}

.button-group ion-button {
  flex: 1;
}

.loading-spinner {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
